import {
  ChangeEvent,
  ComponentProps,
  useEffect,
  useRef,
  useState,
} from "react";
import { Input } from "../ui/input";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

/**
 * InputDebounced is a drop-in replacement for {@link Input} that reports
 * changes only after the user stops typing.
 *
 * Why: with a controlled input the parent re-renders on every keystroke, and in
 * a large tree (a table of rows, a page rendering from the RTK Query cache)
 * that render is what makes typing feel laggy. Buffering here keeps the
 * keystroke path local — one leaf re-render per character — and calls `onChange`
 * once per `delay`, so the expensive render happens a few times per second
 * instead of once per character.
 *
 * The props are `Input`'s, unchanged, plus `delay`. `onChange` receives the
 * change event of the keystroke that started the debounce window, with
 * `currentTarget` restored (React nulls it once a handler returns) — so both
 * `e.target.value` and `e.currentTarget.value` read the value being flushed.
 * What belongs to that one keystroke rather than to the flush: `nativeEvent`
 * and `timeStamp`. Calling `preventDefault` on it has no effect, as it would on
 * any change event.
 *
 * The value is re-synced when it changes from the outside (initial load, a save
 * round-trip, a copy, a rejected save resetting the cache), but never while the
 * field is focused or a flush is still pending, so an in-progress edit is never
 * clobbered.
 *
 * A pending value is flushed on blur and on unmount, so an edit is not lost
 * when the field goes away — a row is deleted, a dialog closes. Note that the
 * unmount flush can only reach the parent; whether it still reaches the server
 * depends on the parent's save mechanism outliving this component (it does not
 * when the whole page unmounts, e.g. on navigation).
 *
 * Drive a debounced save from `onChange` as usual — the two debounces stack, so
 * keep the save delay short enough that the total stays reasonable.
 *
 * @example
 * <InputDebounced
 *   value={row.description}
 *   onChange={(e) => onRowChange({ ...row, description: e.target.value })}
 * />
 */
export function InputDebounced({
  value,
  onChange,
  onFocus,
  onBlur,
  delay = 300,
  ...props
}: ComponentProps<"input"> & {
  /** Milliseconds of inactivity before `onChange` is called. */
  delay?: number;
}) {
  const [text, setText] = useState(() => toText(value));

  const focused = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The value the parent was last told about. Guards against reporting a value
  // it already has — a blur without an edit must not trigger a save.
  const propagated = useRef(toText(value));
  // Held across the debounce window so the flush can hand the caller a real
  // change event. Safe since React 17: synthetic events are no longer pooled.
  const event = useRef<ChangeEvent<HTMLInputElement> | null>(null);

  // Mirrored so `flush` can stay identity-stable and still read the current
  // text and handler (it is called from a timer, from blur and from unmount).
  const textRef = useRef(text);
  textRef.current = text;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const flush = () => {
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    const pending = event.current;
    if (!pending || textRef.current === propagated.current) {
      return;
    }
    propagated.current = textRef.current;
    // React nulls `currentTarget` as soon as the originating handler returns,
    // so restore it before replaying: `e.currentTarget.value` is the common
    // idiom and would throw otherwise. `target` is the input element, whose
    // value is the text being flushed.
    pending.currentTarget = pending.target;
    onChangeRef.current?.(pending);
  };
  const flushRef = useRef(flush);
  flushRef.current = flush;

  // Re-sync from the outside, but never over an edit that is still in flight:
  // while the field is focused the user owns the value, and while a flush is
  // pending the parent has not seen the latest keystrokes yet.
  useRefEffect(value, () => {
    if (focused.current || timer.current !== null) {
      return;
    }
    propagated.current = toText(value);
    setText(propagated.current);
  });

  // Do not drop a pending edit when the field disappears. `flush` only touches
  // refs, so reading it through one keeps this effect free of dependencies.
  useEffect(() => () => flushRef.current(), []);

  return (
    <Input
      {...props}
      value={text}
      onChange={(e) => {
        event.current = e;
        setText(e.target.value);
        textRef.current = e.target.value;
        if (timer.current !== null) {
          clearTimeout(timer.current);
        }
        timer.current = setTimeout(() => {
          timer.current = null;
          flushRef.current();
        }, delay);
      }}
      onFocus={(e) => {
        focused.current = true;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        focused.current = false;
        flushRef.current();
        onBlur?.(e);
      }}
    />
  );
}

/**
 * Normalises the incoming value to the string the field displays: `undefined`
 * would make the input uncontrolled until the first keystroke, and a `number`
 * would not compare equal to the string state the field reports back.
 */
function toText(value: ComponentProps<"input">["value"]): string {
  return String(value ?? "");
}
